import { get, post, put } from './request';

export interface WebsiteInfo {
  websiteDomain?: string;
  websiteName?: string;
  websiteDesc?: string;
  websiteLogo?: string;
  websiteIcon?: string;
  websiteUserAgreement?: string;
  websitePrivacyPolicy?: string;
  rsaLoginPublicKey?: string;
}

export interface VerCodeResp {
  verToken: string;
  img: string;
}

export interface PageResult<T> {
  list: T[];
  totalCount: number;
  totalPage: number;
  pageCurrent: number;
  pageSize: number;
}

export interface CategoryResp {
  id: string | number;
  parentId: string | number;
  categoryName: string;
  categoryIcon?: string;
  categoryBg?: string;
  remark?: string;
  list?: CategoryResp[];
}

export interface CourseListResp {
  id: string | number;
  gmtCreate?: string;
  gmtModified?: string;
  lecturerId: string | number;
  categoryId: number;
  courseName: string;
  courseLogo?: string;
  introduce?: string;
  isFree: number;
  rulingPrice?: number;
  coursePrice?: number;
  memberPrice?: number;
  courseLevel?: string;
  courseLanguage?: string;
  learnCount?: number;
  lectureInfo?: {
    lecturerName?: string;
    lecturerPosition?: string;
    lecturerHead?: string;
    tags?: string;
    languages?: string;
    expertiseAreas?: string;
  };
}

export interface CourseResp extends CourseListResp {
  allowStudy: number;
  learningObjectives?: string;
  learningRequirements?: string;
  discountEndTime?: string;
  courseLevel?: string;
  courseLanguage?: string;
  courseIncludes?: string;
  courseInfo?: string;
  countBuy?: number;
  countStudy?: number;
  lecturerResp?: {
    id?: string | number;
    lecturerName?: string;
    lecturerPosition?: string;
    lecturerHead?: string;
    introduce?: string;
    introduceVideo?: string;
    tags?: string;
    languages?: string;
    timezone?: string;
    price?: number;
    priceUnit?: string;
    expertiseAreas?: string;
    canBook?: number;
  };
  chapterRespList?: Array<{
    id: string | number;
    chapterName?: string;
    chapterDesc?: string;
    periodRespList?: Array<{
      id: string | number;
      periodName?: string;
      periodDesc?: string;
      isFree?: number;
      resourceResp?: {
        videoLength?: number;
      };
    }>;
  }>;
  courseCollect?: boolean;
}

export interface CourseCommentResp {
  id: string | number;
  commentText: string;
  usersVO?: {
    nickname?: string;
    userHead?: string;
  };
  gmtCreate?: string;
}

export interface LecturerResp {
  id: string | number;
  lecturerName?: string;
  lecturerPosition?: string;
  lecturerHead?: string;
  introduce?: string;
  introduceVideo?: string;
  tags?: string;
  languages?: string;
  timezone?: string;
  price?: number;
  priceUnit?: string;
  expertiseAreas?: string;
  canBook?: number;
  courseViewVO?: Array<{
    id: string | number;
    courseName?: string;
    courseLogo?: string;
    introduce?: string;
    rulingPrice?: number;
    coursePrice?: number;
  }>;
}

export interface UsersLoginResp {
  token: string;
  email?: string;
  mobile?: string;
}

export interface AuthUsersResp {
  id: number;
  nickname?: string;
  email?: string;
  mobile?: string;
  userHead?: string;
  userSex?: number;
  userAge?: string;
  remark?: string;
  availableAmount?: number;
  member?: boolean;
  memberPlanCode?: string;
  memberPlanName?: string;
  memberExpireTime?: string;
}

export interface AuthUsersEditPayload {
  nickname: string;
  userSex: number;
  userAge: string;
  userHead?: string;
  remark?: string;
}

export interface AuthUserCourseResp {
  id: string | number;
  courseId: string | number;
  gmtCreate?: string;
  courseProgress?: number;
  periodName?: string;
  courseResp?: {
    id: string | number;
    courseName?: string;
    courseLogo?: string;
    introduce?: string;
    coursePrice?: number;
    rulingPrice?: number;
    lecturerResp?: CourseResp['lecturerResp'];
    chapterRespList?: CourseResp['chapterRespList'];
  };
}

export interface AuthOrderInfoResp {
  orderNo: string | number;
  courseId?: string | number;
  courseName?: string;
  courseLogo?: string;
  coursePrice?: number;
  rulingPrice?: number;
  orderStatus?: number;
  payType?: number;
  gmtCreate?: string;
  payTime?: string;
}

export interface AuthOrderPayResp {
  orderNo: string | number;
  orderStatus?: number;
  courseId?: string | number;
  coursePrice?: number;
  rulingPrice?: number;
  payType?: number;
  payMessage?: string;
  serialNumber?: string | number;
}

export interface AuthMentorBookingResp {
  id: string | number;
  bookingNo: string | number;
  lecturerId: string | number;
  lecturerName?: string;
  lecturerHead?: string;
  lecturerPosition?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  preferTime?: string;
  consultGoal?: string;
  bookingDate?: string;
  bookingTime?: string;
  bookingTimezone?: string;
  consultPrice?: number;
  consultDuration?: string;
  orderStatus?: number;
  payType?: number;
  serialNumber?: string | number;
  remarkCus?: string;
  payTime?: string;
  bookingStatus?: number;
  cancelReason?: string;
  confirmNote?: string;
  gmtCreate?: string;
}

export interface AuthMentorBookingTimeUpdatePayload {
  id: string | number;
  bookingDate: string;
  bookingTime: string;
  bookingTimezone?: string;
  preferTime?: string;
}

export interface AuthMentorBookingPayResp {
  id: string | number;
  bookingNo: string | number;
  lecturerId: string | number;
  lecturerName?: string;
  consultPrice?: number;
  consultDuration?: string;
  orderStatus?: number;
  payType?: number;
  serialNumber?: string | number;
  payMessage?: string;
}

export interface ZoneResp {
  id: number;
  zoneName?: string;
  zoneDesc?: string;
  courseList?: Array<{
    id: string | number;
    courseName?: string;
    courseLogo?: string;
    rulingPrice?: number;
    coursePrice?: number;
    countStudy?: number;
  }>;
}

export interface CarouselResp {
  id: string | number;
  carouselTitle?: string;
  carouselImg?: string;
  carouselUrl?: string;
}

export interface WebsiteArticleCategoryResp {
  id: string | number;
  categoryName?: string;
  categoryDesc?: string;
  icon?: string;
  gradient?: string;
  articleCount?: number;
}

export interface WebsiteArticlePageResp {
  id: string | number;
  categoryName?: string;
  title?: string;
  excerpt?: string;
  tags?: string[];
  authorName?: string;
  authorRole?: string;
  authorCompany?: string;
  authorAvatar?: string;
  coverImage?: string;
  publishedAt?: string;
  readTime?: number;
  views?: number;
  likes?: number;
  featured?: number;
}

export interface WebsiteArticleViewResp extends WebsiteArticlePageResp {
  categoryId?: number;
  content?: string;
}

export interface WebsiteArticleStatsResp {
  id: string | number;
  views?: number;
  likes?: number;
}

export interface WebsiteContactSubmitPayload {
  formType: number;
  companyName?: string;
  contactName: string;
  email: string;
  phone?: string;
  cooperationType?: string;
  companySize?: string;
  currentRole?: string;
  linkedinUrl?: string;
  expertiseArea?: string;
  experienceYears?: string;
  issueType?: string;
  orderNo?: string;
  message?: string;
}

export interface MembershipPlanResp {
  id: string | number;
  planCode?: string;
  planName?: string;
  planDesc?: string;
  billingCycle?: string;
  durationDays?: number;
  originalPrice?: number;
  planPrice?: number;
  badgeText?: string;
  highlightText?: string;
  featureList?: string;
  buttonText?: string;
  isRecommend?: number;
}

export interface MembershipOrderResp {
  id: string | number;
  orderNo: string | number;
  planId?: string | number;
  planCode?: string;
  planName?: string;
  durationDays?: number;
  originalPrice?: number;
  payPrice?: number;
  orderStatus?: number;
  payType?: number;
  remarkCus?: string;
  payTime?: string;
  memberStartTime?: string;
  memberEndTime?: string;
  gmtCreate?: string;
}

export interface MembershipOrderPayResp {
  orderNo: string | number;
  orderStatus?: number;
  serialNumber?: string | number;
  planId?: string | number;
  planName?: string;
  payPrice?: number;
  originalPrice?: number;
  payType?: number;
  memberEndTime?: string;
  payMessage?: string;
}

export const api = {
  getWebsiteInfo: () => get<WebsiteInfo>('/system/api/sys/config/website'),
  getVerCode: () => get<VerCodeResp>('/system/api/common/code'),
  getCategories: () => get<CategoryResp[]>('/course/api/category/list'),
  getZones: () => post<ZoneResp[]>('/course/api/zone/list'),
  getCarousels: () => get<CarouselResp[]>('/system/api/website/carousel/list'),
  getNavs: () => get<Array<{ id: number; navTitle?: string; navUrl?: string }>>('/system/api/website/nav/list'),
  getWebsiteArticleCategories: () => get<WebsiteArticleCategoryResp[]>('/system/api/website/article/category/list'),
  searchWebsiteArticles: (payload: Record<string, unknown>) => post<PageResult<WebsiteArticlePageResp>>('/system/api/website/article/search', payload),
  getWebsiteArticleDetail: (id: string | number) => get<WebsiteArticleViewResp>('/system/api/website/article/view', { id }),
  likeWebsiteArticle: (id: string | number) => post<WebsiteArticleStatsResp>(`/system/api/website/article/like?id=${encodeURIComponent(String(id))}`),
  subscribeWebsite: (payload: Record<string, unknown>) => post<string>('/system/api/website/subscribe/save', payload),
  submitWebsiteContact: (payload: WebsiteContactSubmitPayload) => post<string>('/system/api/website/contact/submit', payload),
  searchCourses: (payload: Record<string, unknown>) => post<PageResult<CourseListResp>>('/course/api/course/search', payload),
  getCourseDetail: (courseId: string | number, authed = false) =>
    post<CourseResp>(authed ? '/course/auth/course/view' : '/course/api/course/view', { courseId }),
  getCourseComments: (courseId: string | number, pageCurrent = 1, pageSize = 20) =>
    post<PageResult<CourseCommentResp>>('/course/api/course/comment', { courseId, pageCurrent, pageSize }),
  searchLecturers: (payload: Record<string, unknown>) => post<PageResult<LecturerResp>>('/user/api/lecturer/search', payload),
  getLecturerDetail: (id: string) => get<LecturerResp>('/user/api/lecturer/view', { id }),
  login: (payload: Record<string, unknown>) => post<UsersLoginResp>('/user/api/users/login', payload),
  googleLogin: (payload: Record<string, unknown>) => post<UsersLoginResp>('/user/api/users/googleLogin', payload),
  sendEmailCode: (payload: Record<string, unknown>) => post<string>('/user/api/users/send/emailCode', payload),
  register: (payload: Record<string, unknown>) => post<UsersLoginResp>('/user/api/users/register', payload),
  getCurrentUser: () => get<AuthUsersResp>('/user/auth/users/view'),
  updateCurrentUser: (payload: AuthUsersEditPayload) => post<string>('/user/auth/users/edit', payload),
  getMembershipPlans: () => get<MembershipPlanResp[]>('/user/api/membership/plan/list'),
  getMyCourses: (payload: Record<string, unknown>) => post<PageResult<AuthUserCourseResp>>('/course/auth/user/course/page', payload),
  getMyOrders: (payload: Record<string, unknown>) => post<PageResult<AuthOrderInfoResp>>('/user/auth/order/info/page', payload),
  getMyMembershipOrders: (payload: Record<string, unknown>) => post<PageResult<MembershipOrderResp>>('/user/auth/membership/order/page', payload),
  getMembershipOrderDetail: (id: string | number) => get<MembershipOrderResp>('/user/auth/membership/order/view', { id }),
  createMembershipOrder: (payload: Record<string, unknown>) => post<MembershipOrderPayResp>('/user/auth/membership/order/create', payload),
  continueMembershipOrderPay: (payload: Record<string, unknown>) => post<MembershipOrderPayResp>('/user/auth/membership/order/continue', payload),
  getOrderDetail: (orderNo: string | number) => get<AuthOrderInfoResp>('/user/auth/order/info/view', { orderNo }),
  createOrder: (payload: Record<string, unknown>) => post<AuthOrderPayResp>('/user/auth/order/pay/create', payload),
  continueOrderPay: (payload: Record<string, unknown>) => post<AuthOrderPayResp>('/user/auth/order/pay/continue', payload),
  cancelOrder: (payload: Record<string, unknown>) => put<string>('/user/auth/order/pay/cancel', payload),
  getMyBookings: (payload: Record<string, unknown>) => post<PageResult<AuthMentorBookingResp>>('/user/auth/mentor/booking/page', payload),
  getMyBookingDetail: (id: string | number) => get<AuthMentorBookingResp>('/user/auth/mentor/booking/view', { id }),
  getActiveBookingByLecturer: (lecturerId: string | number) => get<AuthMentorBookingResp | null>('/user/auth/mentor/booking/active', { lecturerId }),
  createMentorBooking: (payload: Record<string, unknown>) => post<AuthMentorBookingPayResp>('/user/auth/mentor/booking/create', payload),
  continueMentorBookingPay: (payload: Record<string, unknown>) => post<AuthMentorBookingPayResp>('/user/auth/mentor/booking/continue', payload),
  updateMentorBookingTime: (payload: AuthMentorBookingTimeUpdatePayload) => put<string>('/user/auth/mentor/booking/time/update', payload),
  cancelMentorBooking: (payload: Record<string, unknown>) => put<string>('/user/auth/mentor/booking/cancel', payload)
};
